// Calls made by the unauthenticated public kiosk screen. Deliberately separate from
// core/utils/api.ts: every helper there requires a KeycloakContext (it calls
// keycloak.refreshToken()), which doesn't exist on this screen — it authenticates with a
// long-lived X-Display-Token header instead, obtained via the pairing handshake below.

const DISPLAY_TOKEN_HEADER = 'X-Display-Token';

interface StartPairingResponse {
  pairingCode: string;
  pollToken: string;
  expiresInSeconds: number;
}

interface PollPairingResponse {
  status: 'pending' | 'expired' | 'paired';
  displayToken?: string;
  displayId?: string;
}

export interface FacilityDisplayViewConfig {
  status: 'live' | 'paused';
  displayId: string;
  name: string;
  facilityId: string;
  facilityName?: string;
}

export interface FacilityReservationView {
  id: string;
  daysSinceEpoch: number;
  startTime: string;
  endTime: string;
  interval: number;
  title?: string;
  user?: {
    firstName?: string;
    lastName?: string;
    officeName?: string;
  };
}

export interface FacilityDisplayViewData {
  displayId: string;
  facilityId: string;
  facilityName?: string;
  computedStatus: 'Free' | 'Busy' | 'Soon' | 0 | 1 | 2;
  now?: FacilityReservationView;
  next?: FacilityReservationView;
  reservations: FacilityReservationView[];
}

const baseUrl = (): string | undefined => process.env.REACT_APP_BASE_URL;

// Sent as a naive "wall clock" string (no timezone offset/Z suffix) — the backend takes these
// numbers as-is rather than reinterpreting them, so this must use the browser's LOCAL date/time
// components (not toISOString(), which converts to UTC and would reintroduce the same mismatch
// this parameter exists to avoid).
const formatClientNow = (date: Date): string => {
  const pad = (value: number): string => value.toString().padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
};

export const startPairing = async (): Promise<StartPairingResponse | undefined> => {
  if (baseUrl() === undefined) {
    return undefined;
  }

  try {
    const response = await fetch(`${baseUrl()}/facility-display/pairing/start`, { method: 'POST' });
    if (!response.ok) {
      return undefined;
    }
    return await response.json();
  } catch {
    return undefined;
  }
};

export const pollPairing = async (pollToken: string): Promise<PollPairingResponse | undefined> => {
  if (baseUrl() === undefined) {
    return undefined;
  }

  try {
    const response = await fetch(`${baseUrl()}/facility-display/pairing/poll`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pollToken }),
    });
    if (!response.ok) {
      return undefined;
    }
    return await response.json();
  } catch {
    return undefined;
  }
};

// A paired screen sends its token; a public shared link sends only the display id (the backend
// honours the id path only when that display is opted into public sharing). Never both — a public
// viewer is deliberately never issued or stored a token, so sharing a link can't leak a credential.
const accessHeaders = (displayToken?: string): Record<string, string> =>
  (displayToken ? { [DISPLAY_TOKEN_HEADER]: displayToken } : {});

const accessQuery = (displayToken: string | undefined, publicDisplayId: string | undefined): string =>
  (!displayToken && publicDisplayId ? `displayId=${encodeURIComponent(publicDisplayId)}` : '');

export const getFacilityDisplayViewConfig = async (
  displayToken?: string,
  publicDisplayId?: string,
): Promise<{ status: number; config?: FacilityDisplayViewConfig }> => {
  if (baseUrl() === undefined) {
    return { status: 500 };
  }

  const query = accessQuery(displayToken, publicDisplayId);

  try {
    const response = await fetch(`${baseUrl()}/facility-display/view${query ? `?${query}` : ''}`, {
      headers: accessHeaders(displayToken),
    });
    if (!response.ok) {
      return { status: response.status };
    }
    return { status: response.status, config: await response.json() };
  } catch {
    return { status: -1 };
  }
};

export const getFacilityDisplayViewData = async (
  displayToken: string | undefined,
  startDaysSinceEpoch?: number,
  endDaysSinceEpoch?: number,
  publicDisplayId?: string,
): Promise<{ status: number; data?: FacilityDisplayViewData }> => {
  if (baseUrl() === undefined) {
    return { status: 500 };
  }

  const query = new URLSearchParams();
  if (startDaysSinceEpoch !== undefined) {
    query.set('startDaysSinceEpoch', startDaysSinceEpoch.toString());
  }
  if (endDaysSinceEpoch !== undefined) {
    query.set('endDaysSinceEpoch', endDaysSinceEpoch.toString());
  }
  query.set('clientNow', formatClientNow(new Date()));
  if (!displayToken && publicDisplayId) {
    query.set('displayId', publicDisplayId);
  }
  const queryString = query.toString();

  try {
    const response = await fetch(`${baseUrl()}/facility-display/view/data${queryString ? `?${queryString}` : ''}`, {
      headers: accessHeaders(displayToken),
    });
    if (!response.ok) {
      return { status: response.status };
    }
    return { status: response.status, data: await response.json() };
  } catch {
    return { status: -1 };
  }
};
